// src/components/SubmitGarden.tsx

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    IconButton,
    Alert,
    Stack,
    Tooltip,
    TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import ClearIcon from '@mui/icons-material/Clear';
import HeightIcon from '@mui/icons-material/Height';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import { themeColors } from '../styles/theme';
import { ecefToGeodetic, convertToInternalCoords, convertToExternalCoords, disposeObject3D, convertFromExternalCoords } from '../utils/threeUtils';
import * as THREE from 'three';
import { createPolygonVisualization } from '../utils/gardenShapeUtils';
import { 
    getFullMeshes, 
    submitGardenToDatabase, 
    invalidatePubRaycastingCache, 
    deleteGarden,
    updateGardenUncertainty,
} from '../API';
import MultiMeshViewer from './MultiMeshViewer';

export interface SubmitGardenRef {
    startDrawing: () => void;
}

interface SubmitGardenProps {
    googlePlaceId: string;
    onExitAndRefresh: () => void;
    onCancel?: () => void;
    latitude?: number;
    longitude?: number;
    propExistingGardens?: ExistingGarden[];
    reusableMeshData?: FullMeshResponse | null;
    isModerating?: boolean;
}

export interface FullMeshResponse {
    mesh_uris?: string[];
    centre_ecef?: [number, number, number];
    session_id?: string;
    copyrights?: string[];
    has_no_garden?: boolean;
}

interface SubmittedGarden {
    uid: string;
    name: string;
    ecefPolygon: [number, number, number][];
    is_uncertain?: boolean;
}

export interface ExistingGarden {
    uid: string;
    name: string;
    polygon: [number, number][];
    elevation: number;
    is_uncertain?: boolean;
    is_owner?: boolean;
}

type DrawingStep = 'idle' | 'elevation' | 'outline' | 'adjust-height';

const SubmitGarden = React.forwardRef<SubmitGardenRef, SubmitGardenProps>(({ 
    googlePlaceId, 
    onExitAndRefresh, 
    onCancel,
    latitude, 
    longitude,
    propExistingGardens = [],
    reusableMeshData,
}, ref) => {
    const [meshData, setMeshData] = useState<FullMeshResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    const [drawingStep, setDrawingStep] = useState<DrawingStep>('idle');
    const [elevationPoint, setElevationPoint] = useState<[number, number, number] | null>(null);
    const [outlinePoints, setOutlinePoints] = useState<[number, number, number][]>([]);
    const [submittedGardens, setSubmittedGardens] = useState<SubmittedGarden[]>([]);
    const [sceneReady, setSceneReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [selectedEditGarden, setSelectedEditGarden] = useState<ExistingGarden | null>(null);
    const [editingGardenName, setEditingGardenName] = useState<string>('');
    const [heightAdjustment, setHeightAdjustment] = useState(0);

    const sceneDataRef = useRef<{
        scene: THREE.Scene; camera: THREE.PerspectiveCamera; controls: any;
        renderer: THREE.WebGLRenderer; container: HTMLDivElement; localOrigin: THREE.Vector3;
    } | null>(null);

    const markersGroupRef = useRef<THREE.Group | null>(null);
    const outlinesGroupRef = useRef<THREE.Group | null>(null);
    const permanentVisualsGroupRef = useRef<THREE.Group | null>(null);
    const heightAdjustmentGroupRef = useRef<THREE.Group | null>(null);
    const heightIntervalRef = useRef<NodeJS.Timeout | null>(null);
    
    useEffect(() => {
        const processGardens = (gardens: ExistingGarden[]) => {
            const existingAsSubmitted = gardens.map((garden: ExistingGarden) => {
                const ecefPolygon = garden.polygon.map((coord: [number, number]) => {
                    return convertFromExternalCoords([coord[0], coord[1], garden.elevation || 0]).toArray() as [number, number, number];
                });
                return {
                    uid: garden.uid,
                    name: garden.name || 'Garden',
                    ecefPolygon: ecefPolygon,
                    is_uncertain: garden.is_uncertain,
                };
            });
            setSubmittedGardens(existingAsSubmitted);
        };
        processGardens(propExistingGardens);
    }, [propExistingGardens]);

    useEffect(() => {
        if (reusableMeshData) {
            setMeshData(reusableMeshData);
            setLoading(false);
        } else {
            if (!googlePlaceId) return;
            setLoading(true);
            setError(null);
            getFullMeshes(googlePlaceId, latitude, longitude)
                .then(response => setMeshData(response.data))
                .catch(err => setError(err.response?.data?.message || "Unable to load 3D map data."))
                .finally(() => setLoading(false));
        }
    }, [googlePlaceId, latitude, longitude, reusableMeshData]);

    const { dynamicMeshUris, dynamicFocalPoint } = useMemo(() => {
        if (!meshData) return { dynamicMeshUris: [], dynamicFocalPoint: undefined };
        const { mesh_uris = [], session_id = '', centre_ecef } = meshData;
        const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
        const baseUrl = 'https://tile.googleapis.com';
        const completeUrls = mesh_uris.map(uri => {
            if (!session_id || !googleTilesApiKey) return '';
            const cleanSessionId = session_id.startsWith('session=') ? session_id.substring(8) : session_id;
            return `${baseUrl}${uri.startsWith('/') ? uri : `/${uri}`}?session=${cleanSessionId}&key=${googleTilesApiKey}`;
        }).filter(Boolean);
        return { dynamicMeshUris: completeUrls, dynamicFocalPoint: centre_ecef };
    }, [meshData]);

    const initialGardenDataForViewer = useMemo(() => {
        const polygons: { [uid: string]: { polygon: [number, number][], elevation: number | null } } = {};
        const labels: { uid: string, name: string, is_uncertain: boolean, ecefCoordinates: [number, number, number][] }[] = [];

        propExistingGardens.forEach(garden => {
            polygons[garden.uid] = { polygon: garden.polygon, elevation: garden.elevation };
            labels.push({
                uid: garden.uid,
                name: garden.name,
                is_uncertain: !!garden.is_uncertain,
                ecefCoordinates: garden.polygon.map(p => convertFromExternalCoords([p[0], p[1], garden.elevation || 0]).toArray() as [number, number, number])
            });
        });
        return { gardenPolygons: polygons, gardenLabels: labels };
    }, [propExistingGardens]);

    const handleSceneReady = useCallback((data: any) => {
        sceneDataRef.current = data;
        const groupsToCreate = [{ ref: markersGroupRef, name: 'DrawingMarkers' },{ ref: outlinesGroupRef, name: 'DrawingOutlines' },{ ref: permanentVisualsGroupRef, name: 'SubmittedGardens' },{ ref: heightAdjustmentGroupRef, name: 'HeightAdjustment' }];
        groupsToCreate.forEach(({ ref, name }) => {
            if (!ref.current) {
                ref.current = new THREE.Group();
                ref.current.name = name;
                data.scene.add(ref.current);
            }
        });
        setSceneReady(true);
    }, []);

    useEffect(() => {
        const sceneData = sceneDataRef.current;
        const group = permanentVisualsGroupRef.current;
        if (!sceneData || !group || !sceneReady) return;

        disposeObject3D(group);
        permanentVisualsGroupRef.current = new THREE.Group();
        permanentVisualsGroupRef.current.name = 'SubmittedGardens';
        sceneData.scene.add(permanentVisualsGroupRef.current);

        submittedGardens.forEach((garden) => {
            const worldPoints = garden.ecefPolygon.map(p => convertToInternalCoords(p));
            if (worldPoints.length > 0) {
                 const localPoints = worldPoints.map(p => p.clone().sub(sceneData.localOrigin));
                 const { group: polygonGroup } = createPolygonVisualization(localPoints, sceneData.container, null, {
                    fillColor: 0x4caf50, fillOpacity: 0.3, outlineColor: 0x388e3c,
                    outlineWidth: 3, showVertices: false, isSelected: false
                 });
                if (polygonGroup) permanentVisualsGroupRef.current?.add(polygonGroup);
            }
        });
    }, [submittedGardens, sceneReady]);

    const clearDrawingVisuals = useCallback(() => {
        if (outlinesGroupRef.current) disposeObject3D(outlinesGroupRef.current);
        if(sceneDataRef.current?.scene) {
            outlinesGroupRef.current = new THREE.Group();
            outlinesGroupRef.current.name = "DrawingOutlines";
            sceneDataRef.current.scene.add(outlinesGroupRef.current);
        }
    }, []);

    const updateOutline = useCallback(() => {
        if (!sceneDataRef.current || !outlinesGroupRef.current) return;
        clearDrawingVisuals();
        if (outlinePoints.length === 0) return;

        const localPoints = outlinePoints.map(p => convertToInternalCoords(p).sub(sceneDataRef.current!.localOrigin));
        const pointsForPath = [...localPoints, ...(localPoints.length > 2 ? [localPoints[0].clone()] : [])];

        if (pointsForPath.length >= 2) {
            const path = new THREE.CurvePath<THREE.Vector3>();
            for (let i = 0; i < pointsForPath.length - 1; i++) path.add(new THREE.LineCurve3(pointsForPath[i], pointsForPath[i + 1]));
            const tube = new THREE.Mesh(new THREE.TubeGeometry(path, (pointsForPath.length - 1) * 16, 0.1, 8, false), new THREE.MeshBasicMaterial({ color: themeColors.orange, depthTest: false, depthWrite: false, transparent: true, opacity: 0.9 }));
            outlinesGroupRef.current.add(tube);
        }
        localPoints.forEach(p => {
            const vertex = new THREE.Mesh(new THREE.SphereGeometry(0.3, 8, 8), new THREE.MeshBasicMaterial({ color: themeColors.mainYellow, depthTest: false, depthWrite: false }));
            vertex.position.copy(p);
            outlinesGroupRef.current?.add(vertex);
        });
    }, [outlinePoints, clearDrawingVisuals]);

    useEffect(() => { updateOutline(); }, [updateOutline]);

    const handleCanvasClick = useCallback((event: MouseEvent, intersection: THREE.Intersection | null, raycaster: THREE.Raycaster) => {
        if (!sceneDataRef.current || !intersection) return;
        
        if (drawingStep === 'elevation') {
            // Place elevation marker
            const worldPoint = intersection.point.clone();
            const ecefPoint = convertToExternalCoords(worldPoint.add(sceneDataRef.current.localOrigin));
            setElevationPoint(ecefPoint);
            setDrawingStep('outline'); // Move to outline drawing
            return;
        }
        
        if (drawingStep === 'outline' && elevationPoint) {
            const localOrigin = sceneDataRef.current.localOrigin;
            const elevationPlane = new THREE.Plane().setFromNormalAndCoplanarPoint(
                convertToInternalCoords(elevationPoint).normalize(),
                convertToInternalCoords(elevationPoint).sub(localOrigin)
            );
            const newPoint = new THREE.Vector3();
            if (raycaster.ray.intersectPlane(elevationPlane, newPoint)) {
                const ecefCoords = convertToExternalCoords(newPoint.add(localOrigin));
                setOutlinePoints(prev => [...prev, ecefCoords]);
            }
        }
    }, [drawingStep, elevationPoint]);
    
    const handleUndo = () => { if (outlinePoints.length > 0) setOutlinePoints(prev => prev.slice(0, -1)); };
    const handleClear = () => { setOutlinePoints([]); clearDrawingVisuals(); };

    const resetDrawingState = () => {
        setDrawingStep('idle');
        setElevationPoint(null);
        setOutlinePoints([]);
        clearDrawingVisuals();
        setHeightAdjustment(0);
        if (heightAdjustmentGroupRef.current) disposeObject3D(heightAdjustmentGroupRef.current);
    };

    const handleSaveChanges = async () => {
        if (!selectedEditGarden || !editingGardenName.trim()) return;
        setIsSubmitting(true);
        try {
            const gardenLatLon = outlinePoints.map(p => { const [gLat, gLon, gElev] = ecefToGeodetic(p); return [gLat, gLon, gElev] as [number, number, number]; });
            const avgElevation = gardenLatLon.reduce((acc, curr) => acc + curr[2], 0) / gardenLatLon.length;

            await submitGardenToDatabase(googlePlaceId, gardenLatLon, editingGardenName.trim(), avgElevation, selectedEditGarden.is_uncertain, selectedEditGarden.uid, 'MODERATOR');
            invalidatePubRaycastingCache(googlePlaceId);
            resetDrawingState();
            onExitAndRefresh();
        } catch (err) {
            console.error('Error updating garden:', err);
            setError('Failed to update garden. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const visualizeHeightAdjustment = useCallback(() => {
        if (!selectedEditGarden || !sceneDataRef.current || !heightAdjustmentGroupRef.current) return;
        
        disposeObject3D(heightAdjustmentGroupRef.current);
        heightAdjustmentGroupRef.current = new THREE.Group();
        heightAdjustmentGroupRef.current.name = 'HeightAdjustment';
        sceneDataRef.current.scene.add(heightAdjustmentGroupRef.current);
        
        const adjustedElevation = (selectedEditGarden.elevation || 0) + heightAdjustment;
        const ecefPolygon = selectedEditGarden.polygon.map(coord => 
            convertFromExternalCoords([coord[0], coord[1], adjustedElevation]).toArray() as [number, number, number]
        );
        
        const worldPoints = ecefPolygon.map(p => convertToInternalCoords(p));
        if (worldPoints.length > 0) {
            const localPoints = worldPoints.map(p => p.clone().sub(sceneDataRef.current!.localOrigin));
            const { group: polygonGroup } = createPolygonVisualization(localPoints, sceneDataRef.current.container, null, {
                fillColor: 0x1976d2, fillOpacity: 0.5, outlineColor: 0x0d47a1,
                outlineWidth: 4, showVertices: true, isSelected: true,
            });
            if (polygonGroup) heightAdjustmentGroupRef.current.add(polygonGroup);
        }
    }, [selectedEditGarden, heightAdjustment]);

    useEffect(() => {
        if (drawingStep === 'adjust-height') visualizeHeightAdjustment();
    }, [drawingStep, heightAdjustment, visualizeHeightAdjustment]);

    const handleHeightChange = (delta: number) => setHeightAdjustment(prev => prev + delta);
    const handleHeightPressStart = (direction: 'up' | 'down') => {
        const delta = direction === 'up' ? 0.1 : -0.1;
        handleHeightChange(delta);
        heightIntervalRef.current = setInterval(() => handleHeightChange(delta), 100);
    };
    const handleHeightPressEnd = () => { if (heightIntervalRef.current) clearInterval(heightIntervalRef.current); };

    const applyHeightAdjustment = async () => {
        if (!selectedEditGarden || !editingGardenName.trim()) return;
        setIsSubmitting(true);
        try {
            const newElevation = (selectedEditGarden.elevation || 0) + heightAdjustment;
            const gardenLatLon = selectedEditGarden.polygon.map(coord => [coord[0], coord[1], newElevation] as [number, number, number]);
            await submitGardenToDatabase(googlePlaceId, gardenLatLon, editingGardenName.trim(), newElevation, selectedEditGarden.is_uncertain, selectedEditGarden.uid, 'MODERATOR');
            invalidatePubRaycastingCache(googlePlaceId);
            resetDrawingState();
            onExitAndRefresh();
        } catch (err) {
            console.error('Error updating garden elevation:', err);
            setError('Failed to update garden elevation.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteGarden = async () => {
        if (!selectedEditGarden) return;
        if (window.confirm(`Are you sure you want to permanently delete "${selectedEditGarden.name}"?`)) {
            setIsSubmitting(true);
            try {
                await deleteGarden(selectedEditGarden.uid);
                invalidatePubRaycastingCache(googlePlaceId);
                setSelectedEditGarden(null);
                onExitAndRefresh();
            } catch (err) {
                console.error('Error deleting garden:', err);
                setError('Failed to delete garden.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };
    
    const handleToggleUncertainty = async () => {
        if (!selectedEditGarden) return;
        setIsSubmitting(true);
        try {
            const gardenWithLatLon = {
                uid: selectedEditGarden.uid,
                name: selectedEditGarden.name,
                garden_latlon: selectedEditGarden.polygon.map(coord => [coord[0], coord[1], selectedEditGarden.elevation || 0]),
                elevation: selectedEditGarden.elevation,
                is_uncertain: selectedEditGarden.is_uncertain
            };
            await updateGardenUncertainty(googlePlaceId, gardenWithLatLon, !selectedEditGarden.is_uncertain);
            invalidatePubRaycastingCache(googlePlaceId);
            onExitAndRefresh();
        } catch(err) {
            setError("Failed to update uncertainty status.");
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleStartOutlineEdit = () => {
        if (!selectedEditGarden) return;
        
        // Clear any existing drawing state
        setElevationPoint(null);
        setOutlinePoints([]);
        
        // Start with elevation placement step
        setDrawingStep('elevation');
    };

    if (loading) return <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error">{error}</Alert>;

    const renderDrawingUI = () => {
        if (drawingStep === 'idle') return null;

        if (drawingStep === 'elevation') {
            return (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2">Place Elevation Marker for '{editingGardenName}'</Typography>
                    <Typography variant="caption">Click on the map to set the reference elevation point</Typography>
                    <Button onClick={() => { resetDrawingState(); onCancel?.(); }} variant="outlined">Cancel</Button>
                </Paper>
            );
        }

        if (drawingStep === 'outline') {
            return (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2">Drawing New Outline for '{editingGardenName}'</Typography>
                    <Typography variant="caption">Click on the map to place vertices. The outline is drawn at the garden's original elevation.</Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button onClick={handleUndo} disabled={outlinePoints.length === 0} startIcon={<UndoIcon/>}>Undo</Button>
                        <Button onClick={handleClear} disabled={outlinePoints.length === 0} startIcon={<ClearIcon/>} color="warning">Clear</Button>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button onClick={() => { resetDrawingState(); onCancel?.(); }} variant="outlined">Cancel</Button>
                        <Button onClick={handleSaveChanges} variant="contained" disabled={outlinePoints.length < 3 || !editingGardenName.trim() || isSubmitting}>Save Changes</Button>
                    </Stack>
                </Paper>
            );
        }
        
        if (drawingStep === 'adjust-height') {
            return (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography>Adjusting Height for '{editingGardenName}'</Typography>
                    <Typography>Height: {heightAdjustment >= 0 ? '+' : ''}{heightAdjustment.toFixed(1)}m</Typography>
                    <Stack direction="row" spacing={1}>
                        <IconButton onMouseDown={() => handleHeightPressStart('down')} onMouseUp={handleHeightPressEnd} onMouseLeave={handleHeightPressEnd} color="primary"><ArrowDownwardIcon /></IconButton>
                        <IconButton onMouseDown={() => handleHeightPressStart('up')} onMouseUp={handleHeightPressEnd} onMouseLeave={handleHeightPressEnd} color="primary"><ArrowUpwardIcon /></IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Button variant="outlined" onClick={resetDrawingState}>Cancel</Button>
                        <Button variant="contained" onClick={applyHeightAdjustment} disabled={!editingGardenName.trim() || isSubmitting}>Apply Changes</Button>
                    </Stack>
                </Paper>
            );
        }
    };

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <FormControl sx={{ minWidth: 200, flexGrow: 1 }} size="small">
                        <InputLabel>Garden to Edit</InputLabel>
                        <Select
                            value={selectedEditGarden?.uid || ''}
                            onChange={(e) => {
                                const garden = propExistingGardens.find(g => g.uid === e.target.value);
                                setSelectedEditGarden(garden || null);
                                setEditingGardenName(garden?.name || '');
                                resetDrawingState();
                            }}
                            label="Garden to Edit"
                        >
                            {propExistingGardens.map((g) => (
                                <MenuItem key={g.uid} value={g.uid}>{g.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    
                    {selectedEditGarden && (
                        <>
                           <TextField
                                label="Garden Name"
                                value={editingGardenName}
                                onChange={(e) => setEditingGardenName(e.target.value)}
                                size="small"
                                sx={{ minWidth: 200, flexGrow: 1 }}
                            />
                            <Button variant="outlined" size="small" startIcon={<EditIcon/>} onClick={handleStartOutlineEdit}>Edit Outline</Button>
                            <Button variant="outlined" size="small" startIcon={<HeightIcon/>} onClick={() => setDrawingStep('adjust-height')}>Edit Elevation</Button>
                            <Tooltip title={selectedEditGarden.is_uncertain ? "Mark as certain" : "Mark as uncertain"}>
                                <IconButton onClick={handleToggleUncertainty} disabled={isSubmitting}>
                                    {selectedEditGarden.is_uncertain ? <CheckCircleOutlineIcon color="success" /> : <ReportProblemIcon color="warning" />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Garden">
                                <IconButton onClick={handleDeleteGarden} color="error" disabled={isSubmitting}><DeleteIcon /></IconButton>
                            </Tooltip>
                        </>
                    )}
                </Stack>
            </Paper>

            <Box sx={{ position: 'relative', flexGrow: 1 }}>
                {dynamicMeshUris.length > 0 ? (
                    <MultiMeshViewer
                        key={googlePlaceId}
                        meshUris={dynamicMeshUris}
                        focalPoint={dynamicFocalPoint}
                        height="100%"
                        disableShadows={true}
                        gardenPolygons={initialGardenDataForViewer.gardenPolygons}
                        gardenLabels={initialGardenDataForViewer.gardenLabels}
                        onCanvasClick={handleCanvasClick}
                        onSceneReady={handleSceneReady}
                    />
                ) : (
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%'}}>
                        <Typography color="text.secondary">No 3D Map Data Available</Typography>
                    </Box>
                )}
                
                {drawingStep !== 'idle' && (
                    <Box sx={{ position: 'absolute', bottom: 16, left: 16, zIndex: 10 }}>
                        {renderDrawingUI()}
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default SubmitGarden;